/* eslint-disable no-template-curly-in-string */
import React, { useState, useEffect } from "react";
import { Form, Input, Row, Col, Switch, Button, message } from "antd";
import { Title, StyledButton, ButtonContainer } from "./EditTelemedicine.styles";

//import other files and API function
import { lookUpDetails } from "../../../api/LookUpAPI";
import { updateTelemedicineCategory } from "../../../api/TelecategoryAPI";


const EditTelemedicine = (props) => {
  const { categoryId, afterEdit, fetchTelecategoryAfterUpdate } = props;
  const [form] = Form.useForm();
  const [isEligible, setIsEligible] = useState(false);
  const [cdsOptions, setCdsOptions] = useState([]);

  const [fields, setFields] = useState([
    {
      name: ["name"],
      value: "",
    },
    {
      name: ["oneClickPrescribeEligible"],
      value: false,
    },
    {
      name: ["stackAIProjectId"],
      value: "",
    },
    {
      name: ["renewalStackAIProjectId"],
      value: "",
    },
  ]);

  useEffect(() => {
    lookUpDetails("tele-medicine-category", categoryId)
      .then((res) => {
        if (res.data && res.data.payload) {
          const { name = "", oneClickPrescribeEligible = false, stackAIProjectId = "", renewalStackAIProjectId = "", CDS = [] } = res.data.payload;

          setFields([
            { name: ["name"], value: name },
            { name: ["oneClickPrescribeEligible"], value: oneClickPrescribeEligible },
            { name: ["stackAIProjectId"], value: stackAIProjectId },
            { name: ["renewalStackAIProjectId"], value: renewalStackAIProjectId }
          ]);
          setIsEligible(oneClickPrescribeEligible);
          setCdsOptions(CDS);
          message.success("Successfully loaded category details into Edit Telemedicine Category Form");
        }
      })
      .catch(() => {
        message.error("Unable to fetch Telemedicine Category details for Editing");
      });
  }, [categoryId]);

  const onFinish = (values) => {
    const { name, oneClickPrescribeEligible, stackAIProjectId, renewalStackAIProjectId } = values;
    if (oneClickPrescribeEligible) {
      window.alert("Ensure desired US state is also configured for Clinical Eligibility Validator");
    }

    updateTelemedicineCategory(categoryId, { name, oneClickPrescribeEligible, stackAIProjectId, renewalStackAIProjectId, CDS: [...new Set(cdsOptions.map(o => o.trim()).filter(Boolean))] })
      .then(({ data }) => {
        const messageHandler = data?.success ? message.success : message.error;
        const messageString = data?.success 
          ? "Successfully updated Telemedicine Category" 
          : data?.message || "Unable to update Telemedicine Category. Please try again!";
        messageHandler(messageString);
        afterEdit("");
        fetchTelecategoryAfterUpdate();
      })
      .catch((error) => {
        console.log(error);
        const errorMessage = error?.response?.data?.message || "Unable to update Telemedicine Category. Please try again!";
        message.error(errorMessage);
      })
  };

  const onCancelingEdit = () => {
    setFields([
      {
        name: ["name"],
        value: "",
      },
      {
        name: ["oneClickPrescribeEligible"],
        value: false,
      },
      {
        name: ["stackAIProjectId"],
        value: "",
      },
      {
        name: ["renewalStackAIProjectId"],
        value: "",
      },
    ]);
    setIsEligible(false);
    setCdsOptions([]);
    afterEdit("");
  };

  const handleFieldsChange = (_, allFields) => {
    setFields(allFields);
    const eligibleField = allFields.find(field => field.name[0] === 'oneClickPrescribeEligible');
    if (eligibleField !== undefined) {
      const newEligibleValue = eligibleField.value || false;
      setIsEligible(newEligibleValue);
      
      // Clear validation error when switch is turned off
      if (!newEligibleValue) {
        form.setFields([
          { name: 'stackAIProjectId', errors: [] },
          { name: 'renewalStackAIProjectId', errors: [] },
        ]);
      }
    }
  };

  const layout = {
    labelCol: {
      span: 12,
    },
    wrapperCol: {
      span: 24,
    },
  };
  const validateMessages = {
    required: "${label} is required!",
  };

  return (
    <Form
      {...layout}
      form={form}
      layout={"vertical"}
      name="nest-messages"
      onFinish={onFinish}
      fields={fields}
      onFieldsChange={handleFieldsChange}
      validateMessages={validateMessages}
    >
      <Title level={2}>Edit Telemedicine Categories</Title>
      <Row gutter={24}>
        <Col span={24}>
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true }]}
          >
            <Input id="edit-telemedicine-name-input" size={"large"} placeholder="Enter Telemedicine Category" />
          </Form.Item>
        </Col>
        <Col span={24} style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.85)', fontWeight: 'bold' }}>
            CDS Options
          </div>
          {cdsOptions.map((option, index) => (
            <Row key={index} gutter={8} style={{ marginBottom: '8px' }} align="middle">
              <Col flex="auto">
                <Input
                  id={`edit-telemedicine-cds-option-${index}-input`}
                  maxLength={175}
                  value={option}
                  onChange={e => {
                    const updated = [...cdsOptions];
                    updated[index] = e.target.value;
                    setCdsOptions(updated);
                  }}
                  placeholder="Enter CDS option (max 175 characters)"
                  size="large"
                />
              </Col>
              <Col>
                <Button
                  id={`edit-telemedicine-cds-option-${index}-remove-btn`}
                  danger
                  size="large"
                  onClick={() => setCdsOptions(prev => prev.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </Col>
            </Row>
          ))}
          <Button
            id="edit-telemedicine-cds-add-btn"
            type="dashed"
            onClick={() => setCdsOptions(prev => [...prev, ''])}
            style={{ width: '100%' }}
            disabled={cdsOptions.length >= 25}
          >
            + Add CDS Option {cdsOptions.length >= 25 ? '(max 25)' : ''}
          </Button>
        </Col>
        <Col
          span={24}
          style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '20px', 
            borderRadius: '8px' 
          }}
        >
          <div style={{ width: '50%', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Form.Item
              name="oneClickPrescribeEligible"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch id="edit-telemedicine-one-click-prescribe-switch" size="large"/>
            </Form.Item>
            <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>Clinical Eligibility Validator Eligible</span>
          </div>
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Form.Item
              name="stackAIProjectId"
              label="New Case StackAI Project ID"
              style={{ width: '50%' }}
              rules={[{ required: isEligible, message: 'StackAI Project ID is required when Clinical Eligibility Validator is eligible' }]}
            >
              <Input id="edit-telemedicine-stackai-project-id-input" size={"large"} placeholder="Enter StackAI Project ID for initial cases" disabled={!isEligible} />
            </Form.Item>
            <Form.Item
              name="renewalStackAIProjectId"
              label="Renewals StackAI Project ID"
              style={{ width: '50%' }}
            >
              <Input id="edit-telemedicine-renewal-stackai-project-id-input" size={"large"} placeholder="Enter StackAI Project ID for renewals" disabled={!isEligible} />
            </Form.Item>
          </div>
        </Col>
        <ButtonContainer span={24}>
          <Form.Item wrapperCol={{ ...layout.wrapperCol }}>
            <StyledButton
              id="edit-telemedicine-cancel-btn"
              type="secondary"
              size={"large"}
              onClick={onCancelingEdit}
            >
              Cancel
            </StyledButton>
            <StyledButton id="edit-telemedicine-save-btn" type="primary" size={"large"} htmlType="submit">
              Save
            </StyledButton>
          </Form.Item>
        </ButtonContainer>
      </Row>
    </Form>
  );
};

export default EditTelemedicine;
